import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { ArenaCanvas } from "../three/Arena";
import Avatar from "../three/Avatar";
import { useGame, useGameEvent } from "../game/GameProvider";
import { errorToastStyle } from "../utils/utils";
import { unlockAudio, sfx } from "../game/sounds";

const ADJ = ["NEON", "TURBO", "COSMIC", "MEGA", "PIXEL", "HYPER", "DISCO", "LASER"];
const NOUN = ["TIGER", "ROCKET", "PANDA", "NINJA", "COMET", "MANGO", "YETI", "PIRATE"];
const rollRoomName = () =>
  `${ADJ[Math.floor(Math.random() * ADJ.length)]}-${
    NOUN[Math.floor(Math.random() * NOUN.length)]
  }-${Math.floor(Math.random() * 90 + 10)}`;

// Shared screen for both creating and joining a room, with a live 3D
// preview of your player standing on the stage.
const RoomForm = ({ mode }) => {
  const isCreate = mode === "create";
  const [playerName, setPlayerName] = useState("");
  const [roomId, setRoomId] = useState("");
  const navigate = useNavigate();
  const { sendMessage, connected } = useGame();

  useGameEvent("error", (err) => toast.error(err, errorToastStyle));

  const handleSubmit = () => {
    unlockAudio();
    if (!playerName.trim() || !roomId.trim()) {
      toast.error("Please enter your name & room code", errorToastStyle);
      return;
    }
    sendMessage({
      action: isCreate ? "createRoom" : "joinRoom",
      playerName: playerName.trim(),
      roomId: roomId.trim(),
    });
    sfx.join();
    navigate(`/lobby?roomId=${encodeURIComponent(roomId.trim())}`);
  };

  const previewPlayer = {
    id: playerName || "you",
    name: playerName || "YOU",
    team: isCreate ? "left" : "right",
    is_admin: isCreate,
  };

  return (
    <>
      <ArenaCanvas
        cameraBase={[1.6, 2.3, 7]}
        cameraLook={[-1.6, 1.3, 0]}
        sway={1}
        ringColor={isCreate ? "#3dff87" : "#c064ff"}
      >
        <Avatar player={previewPlayer} position={[-2.6, 0, 1.2]} scale={1.25} />
      </ArenaCanvas>

      <div className="form-screen">
        <div className="form-card">
          <div className={`f-kicker ${isCreate ? "lime" : "violet"}`}>
            {isCreate ? "YOU'LL BE THE HOST · GREEN TITANS" : "PICK UP YOUR MIC · JOIN A CREW"}
          </div>
          <h2>{isCreate ? "HOST A SHOW" : "JOIN THE SQUAD"}</h2>

          <div className="f-field">
            <div className="f-label">
              <span>ROOM CODE</span>
              {isCreate && (
                <button
                  className="dice-btn"
                  title="Roll a room name"
                  onClick={() => setRoomId(rollRoomName())}
                >
                  🎲
                </button>
              )}
            </div>
            <input
              className="ainput"
              type="text"
              placeholder={isCreate ? "e.g. NEON-TIGER-42" : "PASTE THE CODE FROM YOUR HOST"}
              value={roomId}
              maxLength={24}
              onChange={(e) => setRoomId(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>

          <div className="f-field">
            <div className="f-label">
              <span>YOUR STAGE NAME</span>
            </div>
            <input
              className="ainput"
              type="text"
              placeholder="WHAT DO WE CALL YOU?"
              value={playerName}
              maxLength={20}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>

          <button
            className={`abtn cta ${isCreate ? "abtn-lime" : "abtn-violet"}`}
            onClick={handleSubmit}
          >
            {isCreate ? "OPEN THE ARENA" : "TAKE THE STAGE"}
          </button>

          <div className="form-foot">
            <Link to="/" className="abtn abtn-ghost">
              ← BACK
            </Link>
            <span className="conn-note">
              <span className={`conn-dot ${connected ? "on" : "off"}`} />
              {connected ? "ARENA ONLINE" : "CONNECTING…"}
            </span>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default RoomForm;
